import { useEffect, useMemo, useState } from "react";
import ApiService from "../../api/apiService";
import { showSupportError } from "./support-helpers";

/** Admin-only: choose which internal users handle customer support. */
function SupportAgentsAdmin({ onClose }) {
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState("");
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    ApiService.support.adminListUsers().then((res) => {
      if (!res.success) return showSupportError(res.message);
      setUsers(res.users);
    });
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (users || [])
      .filter((u) => !q || `${u.display_name} ${u.department || ""}`.toLowerCase().includes(q))
      .sort((a, b) => Number(b.cs_access) - Number(a.cs_access));
  }, [users, filter]);

  const update = async (user, changes) => {
    setSavingId(user.id);
    const res = await ApiService.support.adminUpdateAgent(user.id, changes);
    setSavingId(null);
    if (!res.success) return showSupportError(res.message || res.errors?.[0]?.msg);
    setUsers((list) => list.map((u) => (u.id === user.id ? { ...u, ...res.user } : u)));
  };

  return (
    <section className="cs-admin" aria-label="Manage support agents">
      <header className="cs-admin__head">
        <div>
          <h2>Support agents</h2>
          <p>Only these users see customer chats from Logi. Supervisors can see every chat and take over or reassign any of them.</p>
        </div>
        {onClose && (
          <button type="button" className="cs-btn" onClick={onClose}>
            Close
          </button>
        )}
      </header>
      <input
        className="cs-input"
        type="search"
        placeholder="Search by name or department"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label="Search users"
      />
      {!users ? (
        <p className="cs-empty">Loading users…</p>
      ) : (
        <div className="cs-admin__table-wrap">
          <table className="cs-admin__table">
            <thead>
              <tr>
                <th>User</th>
                <th>Support agent</th>
                <th>Supervisor</th>
                <th>Max chats</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.display_name}</strong>
                    <span>{user.department}</span>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(user.cs_access)}
                      disabled={savingId === user.id}
                      onChange={(e) => update(user, { cs_access: e.target.checked })}
                      aria-label={`Support agent: ${user.display_name}`}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(user.cs_supervisor)}
                      disabled={savingId === user.id || !user.cs_access}
                      onChange={(e) => update(user, { cs_supervisor: e.target.checked })}
                      aria-label={`Supervisor: ${user.display_name}`}
                    />
                  </td>
                  <td>
                    <select
                      value={user.cs_max_concurrent || 3}
                      disabled={savingId === user.id || !user.cs_access}
                      onChange={(e) => update(user, { cs_max_concurrent: Number(e.target.value) })}
                      aria-label={`Max chats: ${user.display_name}`}
                    >
                      {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default SupportAgentsAdmin;
